// Week list + current week view. Polls for changes (visible + online only) and only
// re-renders when the payload actually changed — v1's calm-board behavior.
import { signal } from '@preact/signals';
import { apiGet } from '../lib/api';

export interface CallView {
  id: string;
  slot: string;
  development: string;
  lot: string;
  tech: string;
  src: string;
  name: string;
  phone: string;
  address: string;
  problem: string;
  instructions: string;
}
export interface DayView {
  key: string;
  label: string;
  date: string;
  calls: CallView[];
}
export interface WeekView {
  weekOf: string;
  weekId: string;
  generatedAt: string;
  days: DayView[];
}
export interface WeekListEntry {
  weekOf: string;
  weekId: string;
  count: number;
}

export const weeks = signal<WeekListEntry[]>([]);
export const week = signal<WeekView | null>(null);
export const boardState = signal<'loading' | 'ready' | 'error'>('loading');
export const lastUpdated = signal<number>(0);

let lastSig = '';
const storedWeek = (): string => localStorage.getItem('viewWeek') || '';

export async function loadWeeks(): Promise<void> {
  const res = await apiGet<{ default: string; weeks: WeekListEntry[] }>('/api/schedule?list=1');
  weeks.value = res.weeks;
}

export async function loadWeek(weekId?: string): Promise<void> {
  const target = weekId ?? storedWeek();
  try {
    const w = await apiGet<WeekView>(
      `/api/schedule${target ? `?week=${encodeURIComponent(target)}` : ''}`,
    );
    const sig = JSON.stringify(w);
    if (sig !== lastSig) {
      lastSig = sig;
      week.value = w;
    }
    boardState.value = 'ready';
    lastUpdated.value = Date.now();
  } catch (e) {
    // Keep showing the last good board; only surface the error state when empty.
    if (!week.value) boardState.value = 'error';
    throw e;
  }
}

/** Replace the board with a server-returned week view (admin mutations echo the fresh
 *  week back — applying it directly keeps the board coherent without a second GET). */
export function applyWeek(w: WeekView): void {
  lastSig = JSON.stringify(w);
  week.value = w;
  boardState.value = 'ready';
  lastUpdated.value = Date.now();
}

export function pickWeek(weekId: string): void {
  localStorage.setItem('viewWeek', weekId);
  boardState.value = week.value ? 'ready' : 'loading';
  void loadWeek(weekId).catch(() => {});
}

/** Step to the adjacent week in the visible list (bounded; techs never see the past). */
export function stepWeek(dir: 1 | -1): void {
  const list = [...weeks.value].sort((a, b) => a.weekId.localeCompare(b.weekId));
  const cur = week.value?.weekId ?? '';
  const i = list.findIndex((w) => w.weekId === cur);
  const next = list[i + dir];
  if (next) pickWeek(next.weekId);
}
